import React from "react";
import { Bell } from "lucide-react";
import { useAuth0 } from "@auth0/auth0-react";
import {
  fetchPlatformNotifications,
  markPlatformNotificationAsRead,
  markAllPlatformNotificationsAsRead,
  deletePlatformNotifications,
  markSelectedNotificationsAsRead,
} from "./notificationApi";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useToast } from "../feedback/ToastProvider";
import NotificationModal from "./NotificationModal";
import "./NotificationBell.css";

export default function NotificationBell({
  variant = "icon",
  open: controlledOpen,
  onOpenChange,
  onUnreadCountChange,
  enabled = true,
}) {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const { t } = useTranslation("notifications");

  const canLoadNotifications = isAuthenticated && enabled;

  const [notifications, setNotifications] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [refreshing, setRefreshing] = React.useState(false);

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  React.useEffect(() => {
    onUnreadCountChange?.(unreadCount);
  }, [unreadCount, onUnreadCountChange]);

  const [internalOpen, setInternalOpen] = React.useState(false);

  const navigate = useNavigate();
  const { showToast } = useToast();
  const knownNotificationIdsRef = React.useRef(new Set());
  const hasLoadedOnceRef = React.useRef(false);

  const notificationRetryTimerRef = React.useRef(null);
  const notificationFailureCountRef = React.useRef(0);
  const notificationRequestInFlightRef = React.useRef(false);

  const open = controlledOpen ?? internalOpen;

  function setOpen(next) {
    if (onOpenChange) {
      onOpenChange(next);
    } else {
      setInternalOpen(next);
    }
  }

  function scheduleNotificationRetry() {
    if (notificationRetryTimerRef.current) {
      return;
    }

    const failures = notificationFailureCountRef.current;

    const retryDelay = failures <= 1 ? 15000 : failures === 2 ? 30000 : 60000;

    notificationRetryTimerRef.current = window.setTimeout(() => {
      notificationRetryTimerRef.current = null;

      loadNotifications();
    }, retryDelay);
  }

  async function loadNotifications({ scheduleRetry = true } = {}) {
    if (!canLoadNotifications) {
      return false;
    }

    if (notificationRequestInFlightRef.current) {
      return false;
    }

    notificationRequestInFlightRef.current = true;

    const isFirstLoad = !hasLoadedOnceRef.current;

    try {
      if (isFirstLoad) {
        setLoading(true);
      } else {
        setRefreshing(true);
      }

      const accessToken = await getAccessTokenSilently({
        cacheMode: "cache-first",
      });

      const data = await fetchPlatformNotifications({
        accessToken,
      });

      const nextNotifications = Array.isArray(data)
        ? data
        : data?.results || [];

      const newNotifications = hasLoadedOnceRef.current
        ? nextNotifications.filter(
            (item) => !knownNotificationIdsRef.current.has(item.id),
          )
        : [];

      if (newNotifications.length > 0) {
        newNotifications.forEach((notification) => {
          showToast({
            type:
              notification.severity === "ERROR"
                ? "error"
                : notification.severity === "WARNING"
                  ? "warning"
                  : notification.severity === "SUCCESS"
                    ? "success"
                    : "info",

            title: notification.title,

            message: notification.body,

            duration: 9000,

            onClick: notification.action_url
              ? () => handleOpenNotification(notification)
              : undefined,
          });
        });
      }

      knownNotificationIdsRef.current = new Set(
        nextNotifications.map((item) => item.id),
      );

      hasLoadedOnceRef.current = true;

      setNotifications(nextNotifications);

      notificationFailureCountRef.current = 0;

      if (notificationRetryTimerRef.current) {
        window.clearTimeout(notificationRetryTimerRef.current);

        notificationRetryTimerRef.current = null;
      }

      return true;
    } catch (error) {
      if (process.env.NODE_ENV === "development") {
        console.warn("Notifications temporarily unavailable:", error);
      }

      notificationFailureCountRef.current += 1;

      if (scheduleRetry) {
        scheduleNotificationRetry();
      }

      return false;
    } finally {
      notificationRequestInFlightRef.current = false;

      setLoading(false);
      setRefreshing(false);
    }
  }

  React.useEffect(() => {
    if (!canLoadNotifications) {
      return undefined;
    }

    loadNotifications();

    const interval = window.setInterval(() => {
      loadNotifications();
    }, 60000);

    return () => {
      window.clearInterval(interval);

      if (notificationRetryTimerRef.current) {
        window.clearTimeout(notificationRetryTimerRef.current);

        notificationRetryTimerRef.current = null;
      }
    };
  }, [canLoadNotifications]);

  async function handleMarkRead(notification) {
    if (notification.is_read) return;

    const accessToken = await getAccessTokenSilently();

    await markPlatformNotificationAsRead({
      accessToken,
      notificationId: notification.id,
    });

    setNotifications((prev) =>
      prev.map((item) =>
        item.id === notification.id ? { ...item, is_read: true } : item,
      ),
    );
  }

  async function handleMarkSelectedRead(notificationIds) {
    if (!Array.isArray(notificationIds) || notificationIds.length === 0) {
      return;
    }

    const normalizedIds = notificationIds.map(Number);

    const accessToken = await getAccessTokenSilently();

    await markSelectedNotificationsAsRead({
      accessToken,
      notificationIds: normalizedIds,
    });

    const selectedIdSet = new Set(normalizedIds);

    const readAt = new Date().toISOString();

    setNotifications((prev) =>
      prev.map((item) =>
        selectedIdSet.has(Number(item.id))
          ? {
              ...item,
              is_read: true,
              read_at: item.read_at || readAt,
            }
          : item,
      ),
    );

    await loadNotifications({
      scheduleRetry: false,
    });
  }

  async function handleMarkAllRead() {
    const accessToken = await getAccessTokenSilently();
    await markAllPlatformNotificationsAsRead({ accessToken });

    setNotifications((prev) =>
      prev.map((item) => ({
        ...item,
        is_read: true,
      })),
    );
  }

  async function handleDeleteNotifications(notificationIds) {
    const accessToken = await getAccessTokenSilently();

    await deletePlatformNotifications({
      accessToken,
      notificationIds,
    });

    setNotifications((prev) =>
      prev.filter((item) => !notificationIds.includes(item.id)),
    );
  }

  async function handleOpenNotification(notification) {
    try {
      if (!notification.is_read) {
        await handleMarkRead(notification);
      }
    } catch (error) {
      console.error("Could not mark notification as read:", error);
    }

    if (notification.action_url) {
      navigate(notification.action_url);
    }
  }

  React.useEffect(() => {
    if (open && canLoadNotifications) {
      loadNotifications();
    }
  }, [open, canLoadNotifications]);

  if (variant === "menu") {
    return (
      <>
        <span className="platform-notifications__menu-count">
          {unreadCount > 9 ? "9+" : unreadCount}
        </span>

        {open && (
          <NotificationModal
            notifications={notifications}
            loading={loading}
            refreshing={refreshing}
            onMarkRead={handleMarkRead}
            onMarkAllRead={handleMarkAllRead}
            onMarkSelectedRead={handleMarkSelectedRead}
            onDeleteNotifications={handleDeleteNotifications}
            onClose={() => setOpen(false)}
          />
        )}
      </>
    );
  }

  if (variant === "controller") {
    return open ? (
      <NotificationModal
        notifications={notifications}
        loading={loading}
        refreshing={refreshing}
        onMarkRead={handleMarkRead}
        onMarkAllRead={handleMarkAllRead}
        onMarkSelectedRead={handleMarkSelectedRead}
        onDeleteNotifications={handleDeleteNotifications}
        onClose={() => setOpen(false)}
      />
    ) : null;
  }

  return (
    <div
      className={`platform-notifications platform-notifications--${variant}`}
    >
      <button
        type="button"
        className="platform-notifications__bell"
        aria-label={t("modal.title")}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen(true);
        }}
      >
        <Bell size={20} />

        {unreadCount > 0 && (
          <span className="platform-notifications__badge">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <NotificationModal
          notifications={notifications}
          loading={loading}
          refreshing={refreshing}
          onMarkRead={handleMarkRead}
          onMarkAllRead={handleMarkAllRead}
          onMarkSelectedRead={handleMarkSelectedRead}
          onDeleteNotifications={handleDeleteNotifications}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
}
